import { useMutation, useQuery, useQueryClient, type QueryKey, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { CompanyPipelineDto, PipelineCouplesDto, PipelineEmailDto, VenuePipelineDto } from '@lib/dto/pipeline.dto'
import type { PipelineCouplesQuery, PipelineQuery, PutPipelineEmailInput } from '@lib/input/pipeline.input'
import { pipelineService } from '@/services/PipelineService'
import { pipelineKeys } from './queryKeys'

/** A venue's pipeline for a split, view and weeks. Keeps the last one on screen while the next loads. */
export function useVenuePipeline(venueId: string, query: PipelineQuery): UseQueryResult<VenuePipelineDto> {
  return useQuery({
    queryKey: pipelineKeys.numbers(venueId, query),
    queryFn: () => pipelineService.venue(venueId, query),
    enabled: venueId !== '',
    retry: false,
    placeholderData: (previous) => previous,
  })
}

/** The company's pipeline and its venues'. Company-wide roles only. */
export function useCompanyPipeline(query: PipelineQuery): UseQueryResult<CompanyPipelineDto> {
  return useQuery({
    queryKey: pipelineKeys.company(query),
    queryFn: () => pipelineService.company(query),
    retry: false,
    placeholderData: (previous) => previous,
  })
}

/**
 * A page of the couples behind one count, or in one list; nothing is asked until `enabled`.
 * Paging keeps the page on screen until the next arrives; another count, view,
 * split or period starts empty, so one count's couples never show under another's.
 */
export function usePipelineCouples(venueId: string, query: PipelineCouplesQuery, enabled: boolean): UseQueryResult<PipelineCouplesDto> {
  const queryKey = pipelineKeys.couples(venueId, query, query.list ?? query.target ?? '', query.page ?? 1)
  const sameList = (key: QueryKey | undefined): boolean =>
    key !== undefined && key.length === queryKey.length && queryKey.slice(0, -1).every((part, i) => part === key[i])
  return useQuery({
    queryKey,
    queryFn: () => pipelineService.couples(venueId, query),
    enabled: enabled && venueId !== '',
    retry: false,
    placeholderData: (previous, previousQuery) => (sameList(previousQuery?.queryKey) ? previous : undefined),
  })
}

/** Turns the pipeline email on or off, at a company without GoHighLevel; the company page shows the new state. */
export function useSetPipelineEmail(): UseMutationResult<PipelineEmailDto, Error, PutPipelineEmailInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutPipelineEmailInput) => pipelineService.setEmail(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [...pipelineKeys.all, 'company'] }),
  })
}
